import {
  AudioRecorder,
  isAudioRecordingSupported,
} from '@components/AudioRecorder/AudioRecorder';
import { FormItemRule, IFormItem } from '@components/Form/FormItem/FormItem';
import { Validators } from '@components/Form/FormItem/FormItemValidation';
import { FormInstance } from 'antd';

import { AutopsySource, AutopsySourceLabel } from '@/shared/pathology/enums';

const isSource = (form: FormInstance, source: AutopsySource) =>
  form.getFieldValue('source') === source;

const requiredWhen =
  (source: AutopsySource, rule: FormItemRule) =>
  (form: FormInstance): FormItemRule[] =>
    isSource(form, source) ? [rule] : [];

const fileRequired: FormItemRule = {
  validator: (_rule, value) =>
    Array.isArray(value) && value.length
      ? Promise.resolve()
      : Promise.reject(new Error('Выберите файл записи')),
};

const recordingRequired: FormItemRule = {
  validator: (_rule, value) =>
    value instanceof Blob && value.size > 0
      ? Promise.resolve()
      : Promise.reject(new Error('Запишите описание вскрытия с микрофона')),
};

/**
 * Поля выбора источника описания вскрытия: файл записи, запись с микрофона
 * и (при создании акта) ручной ввод текста. Запись с микрофона предлагается,
 * только если браузер её поддерживает.
 */
export const audioSourceSchema = (sources: AutopsySource[]): IFormItem[] => {
  const recordingSupported = isAudioRecordingSupported();
  const available = sources.filter(
    (source) => source !== AutopsySource.RECORD || recordingSupported,
  );

  return [
    {
      label: 'Описание вскрытия',
      key: 'source',
      type: 'radioGroup',
      payload: {
        options: available.map((source) => ({
          label: AutopsySourceLabel[source],
          value: source,
        })),
      },
      extra:
        sources.includes(AutopsySource.RECORD) && !recordingSupported
          ? 'Запись с микрофона недоступна в этом браузере или без HTTPS — загрузите файл записи'
          : undefined,
      validation: [Validators.required('Выберите источник описания')],
    },
    {
      label: 'Файл записи',
      key: 'audio',
      type: 'file',
      placeholder: 'Выберите аудиофайл',
      payload: {
        accept: 'audio/*',
      },
      dependencies: ['source'],
      hidden: (form) => !isSource(form, AutopsySource.UPLOAD),
      validation: requiredWhen(AutopsySource.UPLOAD, fileRequired),
    },
    ...(recordingSupported && sources.includes(AutopsySource.RECORD)
      ? [
          {
            label: 'Запись с микрофона',
            key: 'recording',
            type: 'custom' as const,
            payload: {
              component: AudioRecorder,
            },
            dependencies: ['source'],
            hidden: (form: FormInstance) => !isSource(form, AutopsySource.RECORD),
            validation: requiredWhen(AutopsySource.RECORD, recordingRequired),
          },
        ]
      : []),
    ...(sources.includes(AutopsySource.TEXT)
      ? [
          {
            label: 'Текст описания',
            key: 'transcript',
            type: 'textarea' as const,
            placeholder: 'Опишите результаты вскрытия',
            payload: {
              autoSize: { minRows: 6, maxRows: 16 },
            },
            dependencies: ['source'],
            hidden: (form: FormInstance) => !isSource(form, AutopsySource.TEXT),
            validation: requiredWhen(AutopsySource.TEXT, {
              required: true,
              whitespace: true,
              message: 'Введите текст описания',
            }),
          },
        ]
      : []),
  ];
};
